import React, { useState } from "react";
import axios from "axios";

const emptyForm = {
  firstName: "",
  lastName: "",
  designation: "",
  organization: "",
  email: "",
  message: "",
};

const HUBSPOT_SUBMIT_URL =
  "https://api.hsforms.com/submissions/v3/integration/submit/24248197/eaa44423-64f7-4af8-9dc6-2d7f8d4f88a8";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const NAME_PATTERN = /^[\p{L}][\p{L}\s.'-]*$/u;
const MESSAGE_MAX = 1000;

export const validateField = (name, rawValue) => {
  const value = rawValue.trim();
  switch (name) {
    case "firstName":
      if (!value) return "Please enter your first name.";
      if (value.length < 2) return "First name must be at least 2 characters.";
      if (!NAME_PATTERN.test(value))
        return "First name can only contain letters, spaces, hyphens, and apostrophes.";
      return "";
    case "lastName":
      if (!value) return "Please enter your last name.";
      if (value.length < 2) return "Last name must be at least 2 characters.";
      if (!NAME_PATTERN.test(value))
        return "Last name can only contain letters, spaces, hyphens, and apostrophes.";
      return "";
    case "designation":
      if (!value) return "Please enter your title.";
      if (value.length < 2) return "Title must be at least 2 characters.";
      return "";
    case "organization":
      if (!value) return "Please enter your company.";
      if (value.length < 2) return "Company must be at least 2 characters.";
      return "";
    case "email":
      if (!value) return "Please enter your email.";
      if (!EMAIL_PATTERN.test(value)) return "Please enter a valid email address.";
      return "";
    case "message":
      if (value.length > MESSAGE_MAX)
        return `Please keep this under ${MESSAGE_MAX} characters.`;
      return "";
    default:
      return "";
  }
};

const validateForm = (data) =>
  Object.keys(data).reduce((errors, key) => {
    const error = validateField(key, data[key]);
    if (error) errors[key] = error;
    return errors;
  }, {});

const getHubspotCookie = () => {
  const match = document.cookie.match(/(?:^|;\s*)hubspotutk=([^;]+)/);
  return match ? match[1] : undefined;
};

export const submitToHubspot = (data) => {
  const fields = [
    { name: "firstname", value: data.firstName },
    { name: "lastname", value: data.lastName },
    { name: "jobtitle", value: data.designation },
    { name: "company", value: data.organization },
    { name: "email", value: data.email },
    { name: "message", value: data.message },
  ].filter((f) => f.value);

  return axios.post(HUBSPOT_SUBMIT_URL, {
    fields,
    context: {
      hutk: getHubspotCookie(),
      pageUri: window.location.href,
      pageName: document.title,
    },
  });
};

const MidstreamContactForm = () => {
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState({ type: "", text: "" });
  const [sending, setSending] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (touched[name]) {
      setErrors({ ...errors, [name]: validateField(name, value) });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched({ ...touched, [name]: true });
    setErrors({ ...errors, [name]: validateField(name, value) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm(formData);
    setErrors(formErrors);
    setTouched(Object.keys(emptyForm).reduce((t, k) => ({ ...t, [k]: true }), {}));
    if (Object.keys(formErrors).length) {
      setStatus({ type: "", text: "" });
      const firstInvalid = e.target.querySelector(`[name="${Object.keys(formErrors)[0]}"]`);
      if (firstInvalid) firstInvalid.focus();
      return;
    }

    const cleaned = Object.fromEntries(
      Object.entries(formData).map(([k, v]) => [k, v.trim()])
    );

    setSending(true);
    try {
      await submitToHubspot(cleaned);
      setStatus({ type: "success", text: "Thanks — we'll be in touch shortly." });
      setFormData(emptyForm);
      setErrors({});
      setTouched({});
    } catch (error) {
      console.error("Error making post request:", error.message);
      setStatus({
        type: "error",
        text: "There was a problem sending your request, please try again later.",
      });
    }
    setSending(false);
  };

  const fieldProps = (name) => ({
    name,
    value: formData[name],
    onChange: handleChange,
    onBlur: handleBlur,
    className: errors[name] ? "is-invalid" : undefined,
    "aria-invalid": errors[name] ? "true" : "false",
    "aria-describedby": errors[name] ? `ms-${name}-error` : undefined,
  });

  const fieldError = (name) =>
    errors[name] ? (
      <small id={`ms-${name}-error`} className="ms-field-error" role="alert">
        {errors[name]}
      </small>
    ) : null;

  return (
    <form className="ms-form" onSubmit={handleSubmit} noValidate>
      <div className="ms-form-row">
        <label>
          <span>
            First name <b>*</b>
          </span>
          <input
            type="text"
            placeholder="Jane"
            autoComplete="given-name"
            maxLength={100}
            required
            {...fieldProps("firstName")}
          />
          {fieldError("firstName")}
        </label>
        <label>
          <span>
            Last name <b>*</b>
          </span>
          <input
            type="text"
            placeholder="Doe"
            autoComplete="family-name"
            maxLength={100}
            required
            {...fieldProps("lastName")}
          />
          {fieldError("lastName")}
        </label>
      </div>
      <div className="ms-form-row">
        <label>
          <span>
            Title <b>*</b>
          </span>
          <input
            type="text"
            placeholder="VP Operations, CFO..."
            autoComplete="organization-title"
            maxLength={100}
            required
            {...fieldProps("designation")}
          />
          {fieldError("designation")}
        </label>
        <label>
          <span>
            Company <b>*</b>
          </span>
          <input
            type="text"
            placeholder="Your company"
            autoComplete="organization"
            maxLength={150}
            required
            {...fieldProps("organization")}
          />
          {fieldError("organization")}
        </label>
      </div>
      <label>
        <span>
          Work email <b>*</b>
        </span>
        <input
          type="email"
          placeholder="you@company.com"
          autoComplete="email"
          maxLength={254}
          required
          {...fieldProps("email")}
        />
        {fieldError("email")}
      </label>
      <label>
        <span>
          Which decision costs you the most sleep? <i>(optional)</i>
        </span>
        <textarea
          rows="3"
          placeholder="Plant routing, contract renewals, compression dispatch..."
          {...fieldProps("message")}
        />
        {fieldError("message")}
      </label>

      {status.text && (
        <p className={`ms-form-status is-${status.type}`}>{status.text}</p>
      )}

      <button type="submit" className="ms-btn ms-form-submit" disabled={sending}>
        {sending ? "Sending..." : <>Find out in two weeks &rarr;</>}
      </button>
      <p className="ms-form-note">
        No integration. No long implementation. Our team does the work.
      </p>
    </form>
  );
};

export default MidstreamContactForm;
